"use client";

import { useId, useState } from "react";

interface AccordionItem {
  question: string;
  answer: string;
}

interface AccordionProps {
  items: AccordionItem[];
}

/**
 * Disclosure pattern: the button sits inside the h3 (not the reverse, so
 * AT keeps the heading semantics), with aria-controls wired to a
 * labelled region panel. Answers stay in the DOM when collapsed
 * (height-clamped) so the copy remains crawlable.
 */
export function Accordion({ items }: AccordionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const baseId = useId();

  return (
    <div className="divide-y divide-border">
      {items.map((item, index) => {
        const isOpen = openIndex === index;
        const buttonId = `${baseId}-item-${index}-button`;
        const panelId = `${baseId}-item-${index}-panel`;
        return (
          <div key={index}>
            <h3 className="font-serif text-sm sm:text-base font-medium text-charcoal">
              <button
                type="button"
                id={buttonId}
                className="w-full flex items-center justify-between py-4 sm:py-5 text-left gap-3"
                onClick={() => setOpenIndex(isOpen ? null : index)}
                aria-expanded={isOpen}
                aria-controls={panelId}
              >
                <span>{item.question}</span>
                <svg
                  className={`w-5 h-5 flex-shrink-0 text-muted transition-transform ${
                    isOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </h3>
            <div
              id={panelId}
              role="region"
              aria-labelledby={buttonId}
              className="grid transition-[grid-template-rows] duration-300 ease-in-out"
              style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
            >
              <div className="overflow-hidden">
                <p className="pb-4 sm:pb-5 text-sm text-muted leading-relaxed">
                  {item.answer}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
